import { Controller } from '@hotwired/stimulus'

// A Trix editor in the house look: its toolbar is one row that scrolls sideways on a phone (the controller marks
// which ends still have tools past them, the CSS fades only those), and by default it takes text only — the popup
// bodies strip attachments on save, so a dropped file would vanish. `attachments: true` keeps them (classic popup).
// A compact editor (an Events description) is marked open while focus is inside it, and text running past the closed
// box is marked for the CSS to fade. Open and closed switch only between presses: a press that takes focus out (Save
// just below) or in would otherwise grow or shrink the box under the pointer, and the click would miss.
export default class extends Controller {
  static values = { attachments: Boolean }

  connect() {
    if (!this.attachmentsValue) {
      this.onFile = (event) => event.preventDefault()
      this.element.addEventListener('trix-file-accept', this.onFile)
    }

    this.box = this.element.closest('.rt-editor--compact')
    if (this.box) {
      const sync = () => setTimeout(() => this.syncOpen())
      const release = () => { this.pressing = false; sync() }
      const layout = () => setTimeout(() => this.syncOverflow())
      this.listeners = [
        [this.box, 'focusin', sync], [this.box, 'focusout', sync],
        [document, 'pointerdown', () => { this.pressing = true }, true],
        [document, 'pointerup', release, true], [document, 'pointercancel', release, true],
        [this.element, 'trix-initialize', layout], [window, 'resize', layout]
      ]
      this.listeners.forEach(([target, type, listener, capture]) => target.addEventListener(type, listener, capture))
      layout()
    }

    const toolbar = document.getElementById(this.element.getAttribute('toolbar')) || this.element.previousElementSibling
    this.row = toolbar && toolbar.querySelector('.trix-button-row')
    if (!this.row) return

    this.onScroll = () => this.syncFades()
    this.row.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onScroll)
    this.syncFades()
  }

  disconnect() {
    if (this.onFile) this.element.removeEventListener('trix-file-accept', this.onFile)
    if (this.listeners) {
      this.listeners.forEach(([target, type, listener, capture]) => target.removeEventListener(type, listener, capture))
    }
    if (!this.row) return

    this.row.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onScroll)
  }

  syncOpen() {
    const open = this.box.contains(document.activeElement)
    if (this.pressing || open === this.box.hasAttribute('data-open')) return

    this.box.toggleAttribute('data-open', open)
    this.syncOverflow()
  }

  syncOverflow() {
    this.element.toggleAttribute('data-overflowing', this.element.scrollHeight > this.element.clientHeight + 1)
  }

  syncFades() {
    const { scrollLeft, scrollWidth, clientWidth } = this.row
    this.row.toggleAttribute('data-fade-start', scrollLeft > 1)
    this.row.toggleAttribute('data-fade-end', scrollLeft + clientWidth < scrollWidth - 1)
  }
}
